import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pin, FolderOpen, RefreshCw } from "lucide-react";
import { api } from "@/api/client";
import { useAuthStore, rolesAtLeast } from "@/stores/auth";
import AdAccountCard from "@/components/ad-accounts/AdAccountCard";
import AdAccountTabs from "@/components/ad-accounts/AdAccountTabs";
import AdAccountFilters from "@/components/ad-accounts/AdAccountFilters";
import AdAccountGroup from "@/components/ad-accounts/AdAccountGroup";
import OnboardForm from "@/components/ad-accounts/OnboardForm";
import type { AdAccount, Organization, TabKey } from "@/components/ad-accounts/types";

export default function AdAccountsPage() {
  const me = useAuthStore((s) => s.user)!;
  const canOnboard = rolesAtLeast(me.role, "ORG_ADMIN");
  const qc = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState<TabKey>("all");
  const [q, setQ] = useState("");
  const [orgId, setOrgId] = useState("");

  const list = useQuery({
    queryKey: ["ad-accounts"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/ad-accounts");
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as AdAccount[];
    },
  });

  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/organizations");
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Organization[];
    },
    enabled: me.role === "SUPER_ADMIN",
  });

  const updateAccount = (next: AdAccount) => {
    qc.setQueryData<AdAccount[]>(["ad-accounts"], (prev) =>
      prev ? prev.map((a) => (a.id === next.id ? next : a)) : prev
    );
  };

  const accounts = list.data ?? [];

  // ---- Stats over ALL accounts (subtitle never moves with tabs) ----
  const stats = useMemo(() => ({
    total:     accounts.length,
    permanent: accounts.filter((a) => a.accessType !== "TEMPORARY").length,
    temporary: accounts.filter((a) => a.accessType === "TEMPORARY").length,
    needsReauth: accounts.filter((a) => a.needsReauth).length,
    sandbox:   accounts.filter((a) => a.sandbox).length,
  }), [accounts]);

  // ---- Tab counts (independent of search/org filter for predictability) ----
  const tabCounts = useMemo(() => ({
    all:       accounts.length,
    permanent: accounts.filter((a) => a.accessType !== "TEMPORARY").length,
    temporary: accounts.filter((a) => a.accessType === "TEMPORARY").length,
    sandbox:   accounts.filter((a) => a.sandbox).length,
  }), [accounts]);

  // ---- Apply tab + search + org filters ----
  const filtered = useMemo(() => {
    let out = accounts;
    if (tab === "permanent") out = out.filter((a) => a.accessType !== "TEMPORARY");
    if (tab === "temporary") out = out.filter((a) => a.accessType === "TEMPORARY");
    if (tab === "sandbox")   out = out.filter((a) => a.sandbox);
    if (orgId)               out = out.filter((a) => a.organizationId === orgId);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((a) =>
        (a.name + " " + a.metaAccountId + " " + (a.organizationName ?? "")).toLowerCase().includes(needle)
      );
    }
    return out;
  }, [accounts, tab, orgId, q]);

  const pinned = filtered.filter((a) => a.pinned);
  const others = filtered.filter((a) => !a.pinned);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">选择广告账户</h1>
          <p className="text-sm text-muted-foreground mt-1">
            你可以访问 <strong className="text-foreground">{stats.total}</strong> 个账户
            {" · 永久 "}<strong className="text-foreground">{stats.permanent}</strong>
            {" · 临时 "}<strong className="text-foreground">{stats.temporary}</strong>
            {stats.needsReauth > 0 && (
              <> {" · "}<strong className="text-rose-600">需要重新授权 {stats.needsReauth}</strong></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ["ad-accounts"] })}
            disabled={list.isFetching}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50 flex items-center gap-1.5"
          >
            <RefreshCw className={"w-3.5 h-3.5 " + (list.isFetching ? "animate-spin" : "")} />
            刷新列表
          </button>
          {canOnboard && (
            <button
              onClick={() => setShowForm(true)}
              className="rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium hover:opacity-90"
            >
              + 接入新账户
            </button>
          )}
        </div>
      </div>

      {showForm && canOnboard && (
        <OnboardForm
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            qc.invalidateQueries({ queryKey: ["ad-accounts"] });
          }}
        />
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <AdAccountTabs active={tab} counts={tabCounts} onChange={setTab} />
        <AdAccountFilters
          q={q}
          organizationId={orgId}
          organizations={orgsQuery.data ?? []}
          showOrgFilter={me.role === "SUPER_ADMIN"}
          onQChange={setQ}
          onOrganizationChange={setOrgId}
        />
      </div>

      {list.isLoading && (
        <div className="rounded-lg border border-border bg-background px-4 py-10 text-center text-sm text-muted-foreground">
          加载中…
        </div>
      )}

      {!list.isLoading && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-background px-4 py-10 text-center text-sm text-muted-foreground">
          {accounts.length === 0
            ? (canOnboard ? "尚未接入任何广告账户" : "你尚未被授权访问任何账户")
            : "没有符合筛选条件的账户"}
        </div>
      )}

      {pinned.length > 0 && (
        <AdAccountGroup
          icon={<Pin className="w-3.5 h-3.5" />}
          title="已置顶"
          count={pinned.length}
        >
          {pinned.map((a) => (
            <AdAccountCard key={a.id} account={a} onMutated={updateAccount} />
          ))}
        </AdAccountGroup>
      )}

      {others.length > 0 && (
        <AdAccountGroup
          icon={<FolderOpen className="w-3.5 h-3.5" />}
          title="所有账户"
          count={others.length}
        >
          {others.map((a) => (
            <AdAccountCard key={a.id} account={a} onMutated={updateAccount} />
          ))}
        </AdAccountGroup>
      )}
    </div>
  );
}
